import React, { useState, useEffect } from 'react';
import { IonContent, IonItem, IonList, IonPage, IonRadio, IonRadioGroup, IonLabel, IonRow, IonCol, IonButton, IonGrid, IonProgressBar, IonCheckbox } from '@ionic/react';
import { Header } from '../../components';
import './RegisterExcellentCompanyPage.css';
import http from '../../utils/http-common';
import { get } from '../../utils/storage';
import { useParams } from 'react-router-dom';

interface Question {
    question: string;
    question_text: string;
    possible_answers: string[];
    selected_answer: string | null;
}

interface QuestionBlock {
    id: number;
    title: string;
    questions: Question[];
}

const RegisterExcellentCompanyPage: React.FC = () => {
    const { intervalId } = useParams<{ intervalId: string }>();
    const [blocks, setBlocks] = useState<QuestionBlock[]>([]);
    const [intervalName, setIntervalName] = useState<string>('');
    const [progress, setProgress] = useState(0);
    const [fulfilled, setFulfilled] = useState(0);
    const [answered, setAnswered] = useState(0);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        if (intervalId) {
            fetchQuestions();
        } else {
            console.error('No intervalId found in URL parameters');
            setErrorMessage('Keine Intervall-ID gefunden. Bitte kehren Sie zur vorherigen Seite zurück.');
            setLoading(false);
        }
        const checkAdminStatus = async () => {
            const user = await get('user');
            setIsAdmin(user?.admin === true);
        };
        checkAdminStatus();
    }, [intervalId]);

    const fetchQuestions = async () => {
        try {
            const user = await get('user');
            const pathParts = window.location.pathname.split('/');
            const contactId = pathParts[pathParts.length - 1];
            const response = await http.get(`/excellent-company-questions/${intervalId}/${contactId}`);
            if (response.status === 200) {
                const parsedData = JSON.parse(response.data.interval.questionaire);
                setBlocks(parsedData);
                setIntervalName(response.data.interval.name);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            setErrorMessage('Fehler beim Laden der Fragen. Bitte versuchen Sie es später erneut.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const totalQuestions = blocks.reduce((sum, block) => sum + block.questions.length, 0);
        const answeredCount = blocks.reduce((sum, block) => 
            sum + block.questions.filter(q => q.selected_answer !== null).length, 0);
        const fulfilledCount = blocks.reduce((sum, block) => 
            sum + block.questions.filter(q => q.selected_answer === 'ja').length, 0);
        
        setAnswered(answeredCount);
        setFulfilled(fulfilledCount);
        console.log(answeredCount);
        console.log(totalQuestions);
        setProgress((answeredCount / totalQuestions) * 100);
    }, [blocks]);

    const handleAnswerChange = (blockId: number, questionId: string, value: string) => {
        setBlocks(prevBlocks => 
            prevBlocks.map(block => {
                if (block.id === blockId) {
                    return {
                        ...block,
                        questions: block.questions.map(question => 
                            question.question === questionId 
                                ? { ...question, selected_answer: value }
                                : question
                        )
                    };
                }
                return block;
            })
        );
    };

    const handleSubmit = async () => {
        if (!isAuthorized) {
            setErrorMessage('Bitte bestätigen Sie, dass Sie berechtigt sind, die Fragen zu beantworten.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const user = await get('user');

            if (!user || !user.contact.contact_id) {
                throw new Error('Keine Firmen-ID gefunden. Bitte melden Sie sich erneut an.');
            }

            const pathParts = window.location.pathname.split('/');
            const contactId = pathParts[pathParts.length - 1];

            const response = await http.post('/excellent-company/register', {
                blocks: blocks,
                contact_id: contactId,
                user_id: user.id,
                interval_id: intervalId
            });

            if (response.status === 200) {
                setSuccessMessage('Ihre Registrierung wurde erfolgreich abgeschlossen. Der Fragebogen wird nun vom DeSH geprüft.');

                const certificate = {
                    companyName: user.contact.company_name,
                    contactName: `${user.contact.first_name} ${user.contact.last_name}`,
                    blocks: blocks,
                    interval_id: intervalId
                };
            }
        } catch (error) {
            setErrorMessage(error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegister = async () => {
        try {
            const token = await get('token');
            const pathParts = window.location.pathname.split('/');
            const contactId = pathParts[pathParts.length - 1];

            const response = await http.post('/excellent-company-confirm', {
                contact_id: contactId,
                interval_id: intervalId
            });
            
            if (response.status === 200) {
                window.location.href = '/mitgliederbereich/excellent-companies';
            }
        } catch (error) {
            console.error('Error confirming registration:', error);
        }
    };

    if (loading) {
        return (
            <IonPage>
                <Header name="Exzellente Ausbildungsbetriebe" />
                <IonContent>
                    <div className="ion-padding">Laden...</div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <Header name="Exzellente Ausbildungsbetriebe" />
            <IonContent>
                <IonGrid className="exc-comp-reg-grid">
                    <IonRow>
                        <IonCol size="12" sizeMd="9">
                            <div className="content">
                                {successMessage ? (
                                    <div className="success-message">{successMessage}</div>
                                ) : (
                                    <>
                                        <h2 className="exc-comp-reg-title">Registrieren</h2>
                                        <h3 className="exc-comp-reg-subtitle">für das Ausbildungsjahr {intervalName}</h3>
                                        <p className="exc-comp-reg-description">
                                            Bitte beantworten Sie die folgenden Fragen wahrheitsgemäß auf Basis der aktuellen Situation. 
                                            Nach der Registrierung wird dieser Fragebogen vom DeSH geprüft und im Erfolgsfall das Zertifikat für das aktuelle Ausbildungsjahr erstellt.
                                            Dieses Zertifikat können Sie dann hier herunterladen und frei verwenden. <br></br>
                                            Zusätzlich wird dieser Fragebogen auf der Webseite holztalente.de veröffentlicht.
                                        </p>
                                        
                                        {errorMessage && (
                                            <div className="error-message">{errorMessage}</div>
                                        )}

                                        {blocks.map((block) => (
                                            <IonList key={block.id} inset={true} className="exc-comp-reg-wrapper">
                                                <IonItem className="exc-comp-reg-content-wrapper">
                                                    <div className="exc-comp-reg-question-header">
                                                        <p className="exc-comp-reg-question-label">Modul {block.id}:</p>
                                                        <h2 className="exc-comp-reg-question-title">{block.title}</h2>
                                                    </div>
                                                </IonItem>
                                                
                                                {block.questions.map((question) => (
                                                    <IonItem key={question.question}>
                                                        <IonLabel>
                                                            <span>
                                                                <span className="question-number">{question.question.split('_')[1]})</span> {question.question_text}
                                                            </span>
                                                        </IonLabel>
                                                        <IonRadioGroup 
                                                            value={question.selected_answer} 
                                                            onIonChange={e => handleAnswerChange(block.id, question.question, e.detail.value)}
                                                        >
                                                            <IonRow>
                                                                {question.possible_answers.map((answer) => (
                                                                    <IonCol key={answer}>
                                                                        <IonItem lines="none">
                                                                            <IonLabel>{answer.toUpperCase()}</IonLabel>
                                                                            <IonRadio slot="start" value={answer} />
                                                                        </IonItem>
                                                                    </IonCol>
                                                                ))}
                                                            </IonRow>
                                                        </IonRadioGroup>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        ))}
                                    </>
                                )}
                            </div>
                        </IonCol>
                        <IonCol size="12" sizeMd="3">
                            <div className="sidebar">
                                <div className="progress-container">
                                    <h3>Fortschritt</h3>
                                    <IonProgressBar value={progress / 100} color="primary" />
                                    <p className="progress-text">{answered}/{blocks.reduce((sum, block) => sum + block.questions.length, 0)} Fragen beantwortet</p>
                                </div>
                                <div className="criteria-container">
                                    <h3>Erfüllte Kriterien</h3>
                                    <div className="criteria-count">
                                        <span className="criteria-number">{fulfilled}</span>
                                        <span className="criteria-of">von {blocks.reduce((sum, block) => sum + block.questions.length, 0)}</span>
                                    </div>
                                    {progress === 100 && (
                                        <>
                                            {(fulfilled / blocks.reduce((sum, block) => sum + block.questions.length, 0)) >= 0.8 ? (
                                                <p className="success-message">
                                                    Ihr Unternehmen erfüllt die Anforderungen für die Registrierung
                                                </p>
                                            ) : (
                                                <p className="error-message">
                                                    Ihr Unternehmen erfüllt nicht die Mindestanforderungen für die Registrierung
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                                {!isAdmin ? (
                                    <>
                                        <IonItem lines="none" className="checkbox-item exc-comp-reg-content-wrapper">
                                            <IonLabel className="checkbox-label">
                                                Ich bin berechtigt die Fragen für die Registrierung des Unternehmens zu beantworten
                                            </IonLabel>
                                            <IonCheckbox
                                                slot="start" 
                                                checked={isAuthorized}
                                                onIonChange={e => setIsAuthorized(e.detail.checked)}
                                            />
                                        </IonItem>
                                        <IonButton
                                            expand="block"
                                            color="primary" 
                                            onClick={handleSubmit}
                                            disabled={progress < 100 || isSubmitting || !!successMessage}
                                        >
                                            {isSubmitting ? 'Wird gesendet...' : 'Registrierung abschließen'}
                                        </IonButton>
                                    </>
                                ) : (
                                    <IonButton
                                        expand="block"
                                        color="primary"
                                        onClick={handleRegister}
                                    >
                                        Registrierung freigeben
                                    </IonButton>
                                )}
                            </div>
                        </IonCol>
                    </IonRow>
                </IonGrid>
            </IonContent>
        </IonPage>
    );
};

export default RegisterExcellentCompanyPage; 